import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { HTMLTouchControls } from '../ui/HTMLTouchControls';
import { TileType } from './MazeGenerator';
import { TILE_SIZE } from '../config/game.config';
import { useAlternateControls } from '../utils/DebugConfig';

export class MovementController {
    private scene: Phaser.Scene;
    private player: Player;
    private mazeData: TileType[][];
    private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
    private wasd!: { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key };
    private touchControls?: HTMLTouchControls;

    constructor(scene: Phaser.Scene, player: Player, mazeData: TileType[][]) {
        this.scene = scene;
        this.player = player;
        this.mazeData = mazeData;

        this.setupInput();
    }

    private setupInput(): void {
        if (!this.scene.input.keyboard) return;

        this.cursors = this.scene.input.keyboard.createCursorKeys();
        this.wasd = {
            W: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W),
            A: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
            S: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S),
            D: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D),
        };
    }

    public setTouchControls(controls: HTMLTouchControls): void {
        this.touchControls = controls;
    }

    public update(): void {
        const acceleration = 1200;
        let vx = 0;
        let vy = 0;

        // Keyboard input - WASD/Arrows
        if (this.cursors.left.isDown || this.wasd.A.isDown) {
            vx = -acceleration;
        } else if (this.cursors.right.isDown || this.wasd.D.isDown) {
            vx = acceleration;
        }

        if (this.cursors.up.isDown || this.wasd.W.isDown) {
            vy = -acceleration;
        } else if (this.cursors.down.isDown || this.wasd.S.isDown) {
            vy = acceleration;
        }

        // Touch input (overrides keyboard if active)
        if (this.touchControls?.isActive()) {
            const dir = this.touchControls.getDirection();
            vx = dir.x * acceleration * 1.5;
            vy = dir.y * acceleration * 1.5;
        }

        // Corner sliding assist: if pressing a direction but velocity is near zero,
        // player is blocked - nudge them perpendicular to slide past corners
        const body = this.player.body as Phaser.Physics.Arcade.Body;
        const cornerAssistStrength = 600; // Nudge strength for corner sliding
        const velocityThreshold = 30; // Consider "blocked" if velocity below this

        if (body) {
            const actualVx = body.velocity.x;
            const actualVy = body.velocity.y;

            // Trying to move horizontally but effectively stopped
            const tryingHorizontal = Math.abs(vx) > 0 && vy === 0;
            const blockedHorizontally = Math.abs(actualVx) < velocityThreshold;

            if (tryingHorizontal && blockedHorizontally) {
                // Check which perpendicular direction has open space
                const canGoUp = !this.isWallAt(this.player.x, this.player.y - TILE_SIZE * 0.6);
                const canGoDown = !this.isWallAt(this.player.x, this.player.y + TILE_SIZE * 0.6);

                if (canGoUp && !canGoDown) {
                    vy = -cornerAssistStrength;
                } else if (canGoDown && !canGoUp) {
                    vy = cornerAssistStrength;
                }
            }

            // Trying to move vertically but effectively stopped
            const tryingVertical = Math.abs(vy) > 0 && vx === 0;
            const blockedVertically = Math.abs(actualVy) < velocityThreshold;

            if (tryingVertical && blockedVertically) {
                const canGoLeft = !this.isWallAt(this.player.x - TILE_SIZE * 0.6, this.player.y);
                const canGoRight = !this.isWallAt(this.player.x + TILE_SIZE * 0.6, this.player.y);

                if (canGoLeft && !canGoRight) {
                    vx = -cornerAssistStrength;
                } else if (canGoRight && !canGoLeft) {
                    vx = cornerAssistStrength;
                }
            }
        }

        this.player.setAcceleration(vx, vy);

        // Normalize diagonal movement
        if (vx !== 0 && vy !== 0) {
            this.player.setAcceleration(vx * 0.707, vy * 0.707);
        }
    }

    /**
     * Check if there's a wall at the given world position
     */
    private isWallAt(worldX: number, worldY: number): boolean {
        const tileX = Math.floor(worldX / TILE_SIZE);
        const tileY = Math.floor(worldY / TILE_SIZE);

        // Out of bounds = wall
        if (tileY < 0 || tileY >= this.mazeData.length ||
            tileX < 0 || tileX >= this.mazeData[0].length) {
            return true;
        }

        return this.mazeData[tileY][tileX] === TileType.WALL;
    }

    public destroy(): void {
        // Clean up inputs if needed, though keyboard keys usually persist with scene
    }
}
